import { DragEndEvent } from "@dnd-kit/core";
import { addMinutes, setMinutes, setSeconds } from "date-fns";
import { Event } from "../../types/events";
import { Resource } from "../../types/resources";
import {
  TIMELINE_BASE_DAY_WIDTH,
  TIMELINE_MINUTES_IN_DAY,
  TIMELINE_SNAP_INTERVAL_MINUTES,
} from "./timelineConstants";

interface DndTransform {
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
}

interface DndModifierArgs {
  transform: DndTransform;
}

interface CreateTimelineDragEndHandlerArgs {
  zoom: number;
  getEventResourceIds?: (event: Event) => (string | number)[];
  onEventDrop?: (
    eventId: string,
    newStartTime: Date,
    newResource?: Resource
  ) => void | Promise<any>;
}

const getComparableResourceId = (resourceId: unknown): string => {
  const maybeResource = resourceId as { id?: unknown };
  return String(maybeResource?.id ?? resourceId);
};

export const createTimelineSnapModifier =
  (zoom: number) =>
  ({ transform }: DndModifierArgs): DndTransform => {
    const xSnapSize =
      (TIMELINE_BASE_DAY_WIDTH * zoom) /
      (TIMELINE_MINUTES_IN_DAY / TIMELINE_SNAP_INTERVAL_MINUTES);

    if (!xSnapSize) {
      return transform;
    }

    return {
      ...transform,
      x: Math.round(transform.x / xSnapSize) * xSnapSize,
      // Resource rows can have variable heights when overlapping events create
      // multiple lanes, so vertical snapping must not assume the historical
      // fixed row height. Let dnd-kit collision detection choose the target row.
      y: transform.y,
    };
  };

export const createTimelineDragEndHandler =
  ({
    zoom,
    getEventResourceIds,
    onEventDrop,
  }: CreateTimelineDragEndHandlerArgs) =>
  (dragEvent: DragEndEvent) => {
    const { active, over, delta } = dragEvent;

    if (!over || !active.data.current || !over.data.current) {
      return;
    }

    const draggedItemType = active.data.current.type;
    const dropZoneType = over.data.current.type;
    if (draggedItemType !== "event" || dropZoneType !== "resource-row") {
      return;
    }

    const originalEvent = active.data.current.originalEvent as Event | undefined;
    const targetResource = over.data.current.resource as Resource | undefined;
    if (!originalEvent || !targetResource) {
      return;
    }

    const pixelsPerMinute =
      (TIMELINE_BASE_DAY_WIDTH * zoom) / TIMELINE_MINUTES_IN_DAY;
    if (!pixelsPerMinute) {
      return;
    }

    const originalEventStartTime = new Date(originalEvent.timeRange.start);
    const hasHorizontalMovement = Math.abs(delta.x) > 1;
    let newStartTime = new Date(originalEventStartTime);

    if (hasHorizontalMovement) {
      const minutesMovedEquivalent = delta.x / pixelsPerMinute;
      newStartTime = addMinutes(originalEventStartTime, minutesMovedEquivalent);
      const roundedMinutes =
        Math.floor(newStartTime.getMinutes() / TIMELINE_SNAP_INTERVAL_MINUTES) *
        TIMELINE_SNAP_INTERVAL_MINUTES;
      newStartTime = setSeconds(setMinutes(newStartTime, roundedMinutes), 0);
      newStartTime.setMilliseconds(0);
    }

    const hasTimeChanged =
      Math.abs(newStartTime.getTime() - originalEventStartTime.getTime()) > 1;
    const resourceIds =
      getEventResourceIds?.(originalEvent) ??
      originalEvent.assignedResources ??
      [];
    const targetResourceId = getComparableResourceId(targetResource.id);
    const isNewResource = !resourceIds.some(
      (resourceId) => getComparableResourceId(resourceId) === targetResourceId
    );

    if (isNewResource || hasTimeChanged) {
      onEventDrop?.(originalEvent.id, newStartTime, targetResource);
    }
  };
